import {
  Button,
  Flex,
  Radio,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Input,
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { routes, userRoles } from "../../../../../../constants";
import {
  baseRouteMaker,
  baseRouteMakerForSuperAdmin,
} from "../../../../../../constants/routeConsts";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate } from "../../../../../../utils/utils";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { getWalletTransactionsAction } from "../../../../../../redux/services/wallet-transactions/walletTransactionsServices";
import { MoneyCollectOutlined } from "@ant-design/icons";
import styles from "./WalletManagement.module.scss";
import { getWalletManagementAction } from "../../../../../../redux/services/wallet-management/walletManagementServices";
import Actions from "../../../../../../redux/actions";
import { SearchOutlined } from "@ant-design/icons";
import useDebounce from "../../../../../../hooks/useDebounce";

const WalletManagement = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [searchValue, setSearchValue] = useState("");
  const [columns, setColumns] = useState([]);
  const { walletManagement } = useSelector((state) => state.walletManagement);
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: walletManagement?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* dialog dependencies */
  const [openServiceDeleteDialog, setOpenServiceDeleteDialog] = useState(false);
  /* dialog dependencies */

  const [filterRole, setFilterRole] = useState(userRoles.CUSTOMER);

  /* function definition to get table data */

  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );

  const handleGetTableData = () => {
    setLoading(true);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      role: filterRole,
    };
    dispatch(getWalletManagementAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue, filterRole]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.Provider.ID"),
        dataIndex: "id_number",
        key: "id_number",
      },
      {
        title: t("createAt"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("user"),
        render: (_, record) =>
          record?.role === userRoles.SERVICE_PROVIDER_ADMIN
            ? record?.organization?.name
            : record?.first_name || record?.first_name
            ? `${record?.first_name} ${record?.last_name}`
            : "-",
      },
      {
        title: t("ForgotPassword.emailLabel"),
        dataIndex: "email",
        key: "email",
      },
      {
        title: t("walletInfo.balance"),
        dataIndex: "wallet_balance",
        key: "wallet_balance",
        render: (_, record) => (
          <p>
            {record?.wallet_balance ? record?.wallet_balance?.toFixed(2) : 0}
          </p>
        ),
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Button
                type="primary"
                size="small"
                loading={loading}
                onClick={(e) => handleAddEditDetails(e, record)}
                ghost
              >
                {t("Modules.WalletManagement.addFund")}
              </Button>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleAddEditDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_WALLET_MANAGEMENT_ID, {
        selectedWalletManagementId: "",
      })
    );
    navigate(baseRouteMakerForSuperAdmin(routes.ADD_WALLET_MANAGEMENT));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedWalletManagementId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_WALLET_MANAGEMENT_ID, {
          selectedWalletManagementId,
        })
      );
    }
  };

  return (
    <div className={styles.walletManagementContainer}>
      <Flex vertical gap="2rem">
        <Flex align="center" justify="space-between">
          <Typography.Title level={2}>{t("revnue.walletManagement")}</Typography.Title>
          <Radio.Group
            value={filterRole}
            onChange={(event) => setFilterRole(event.target.value)}
          >
            <Radio value={userRoles.CUSTOMER}>
              {t("Modules.WalletManagement.customer")}
            </Radio>
            <Radio value={userRoles.SERVICE_PROVIDER_ADMIN}>
              {t("Modules.WalletManagement.serviceProvider")}
            </Radio>
          </Radio.Group>
        </Flex>
        <Row gutter={24} justify="space-between">
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByNameEmail")}
              onChange={(event) => setSearchValue(event.target.value)}
              style={{ width: "370px" }}
            />
          </Col>
        </Row>
        {loading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={walletManagement?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default WalletManagement;
